import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Card } from '../common/Card';

interface GeographicData {
  country: string;
  streams: number;
  revenue: number;
}

interface GeographicChartProps {
  data: GeographicData[];
}

const COLORS = ['#FFD700', '#60A5FA', '#34D399', '#F87171', '#A78BFA', '#FBBF24', '#FB7185'];

export const GeographicChart: React.FC<GeographicChartProps> = ({ data }) => {
  const formatTooltipValue = (value: number, name: string) => {
    if (name === 'streams') {
      return [`${value.toLocaleString()}`, 'Streams'];
    }
    return [`$${value.toLocaleString()}`, 'Revenue'];
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return percent > 0.05 ? (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="medium"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white font-display">
          Geographic Distribution
        </h3>
        <div className="text-sm text-gray-400">
          By streams
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="streams"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [
                name === 'streams' ? `${value.toLocaleString()} streams` : `$${value.toLocaleString()}`,
                name === 'streams' ? 'Streams' : 'Revenue'
              ]}
              contentStyle={{
                backgroundColor: '#1F2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#FFFFFF'
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value, entry) => (
                <span style={{ color: entry.color, fontSize: '12px' }}>
                  {value}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Country breakdown */}
      <div className="mt-6 pt-6 border-t border-gray-700">
        <div className="space-y-3">
          {data.map((country, index) => (
            <div key={country.country} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-white font-medium">{country.country}</span>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-gray-400">
                  {country.streams.toLocaleString()} streams
                </span>
                <span className="text-green-400 font-medium">
                  ${country.revenue.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};